import { AppearanceContext } from './appearance-context';
import { useLayoutEffect, useState, useMemo, type ReactNode } from 'react';
import { readAppearance, writeAppearance, resolveAppearance } from './preferences';
import { CLASSIC_VARIANT } from './variants';

export function AppearanceProvider({
  tenantId,
  userId,
  children,
}: {
  tenantId: string;
  userId: string;
  children: ReactNode;
}): JSX.Element {
  const [variant, setVariant] = useState(() => {
    try {
      return readAppearance(window.localStorage, tenantId, userId);
    } catch {
      return CLASSIC_VARIANT;
    }
  });
  useLayoutEffect(() => {
    // Portaled dialogs inherit the same semantic tokens as the authenticated shell.
    document.documentElement.classList.add(variant.className);
    return () => document.documentElement.classList.remove(variant.className);
  }, [variant.className]);
  const value = useMemo(
    () => ({
      variant,
      select: (id: string) => {
        setVariant(resolveAppearance(id));
        try {
          writeAppearance(window.localStorage, tenantId, userId, id);
        } catch {
          /* Some browsers deny access to localStorage itself. */
        }
      },
    }),
    [tenantId, userId, variant],
  );
  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
